import { ReactNode } from "react";

import { JSONSchema7TypeName } from "json-schema";

import { IntegrationProviderResource } from "src/api/connectApi/types";
import { DropDownElement } from "src/base-components/DropDown";
import { DataRetentionUnit } from "src/connections/types";
import type { ConnectNodeBase } from "src/constants/NodeDataTypes";

export const DEFAULT_OUTPUT_BE_NAME = "default" as const;

//BE types
export type OutputMappingBE = {
  id: string;
  active: boolean;
  mapped_to: string;
};

export type Config = {
  caching: {
    active: boolean;
    max_age_seconds: string;
    // Used only for displaying in the UI
    // to properly convert seconds to the correct unit
    unit?: DataRetentionUnit;
  };
  timeout?: {
    active: boolean;
    timeout_seconds: string;
  };
  raw_requests: {
    has_raw_requests_enabled_in_node: boolean;
  };
  error: {
    allow_4xx: boolean;
    allow_5xx: boolean;
  };
  tenant_id?: SingleMappingBE;
};

export type SingleMappingBE = {
  id: string;
  expression: string;
};

export type InputMappingBE = {
  singles: { [key: string]: SingleMappingBE };
  groups: {
    [groupName: string]: InputMappingBE;
  };
  lists: {
    [listName: string]: InputMappingBE[];
  };
};

export type IntegrationResourceBE = {
  provider_resource: IntegrationProviderResource;
  connection_id: string;
  resource_config_id: string;
  input: InputMappingBE;
  output: {
    [DEFAULT_OUTPUT_BE_NAME]: OutputMappingBE;
    [key: string]: OutputMappingBE;
  };
  config: Config;
  files: { [key: string]: OutputMappingBE };
};

export type RenderCondition = {
  key: string;
  key_type?: "node" | "resource";
  value: string[];
};

export type InputType = "text" | "dropDown" | "mappedComplexType";
export type InputMapping = StringInput | DropDownInput | MappedComplexTypeInput;

type BaseInput = {
  id: string;
  type: InputType;
  displayName: string;
  assignedTo: string;
  rules?: InputMappingRules;
  hint?: string;
  beType?: JSONSchema7TypeName | JSONSchema7TypeName[] | undefined;
  example?: string;
  renderConditions?: RenderCondition[];
};

export type StringInput = BaseInput & {
  type: "text";
};

export type DropDownInput = BaseInput & {
  type: "dropDown";
  elements: DropDownElement<string>[];
};

export type MappedComplexTypeInput = BaseInput & {
  type: "mappedComplexType";
};

/**
 * We imitate the react-hook-form rules here for future compatibility. However, we don't use the validation engine.
 * Instead, we use them to restrict the user input in the UI. E.g. disabling the "Delete" button if the field is required.
 */
export type InputMappingRules = {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
};

export type InputMappingList = {
  displayName: string;
  rules?: InputMappingRules;
  description?: string;
  getDefaultElement: () => { [key: string]: InputMapping };
  elements: { [key: string]: InputMapping }[];
  renderConditions?: RenderCondition[];
};

export type InputMappingGroup = {
  getDefaultElements: () => { [key: string]: InputMapping };
  rules?: InputMappingRules;
  displayName?: string;
  description?: string;
  elements?: { [key: string]: InputMapping };
  lists?: { [listName: string]: InputMappingList };
  renderConditions?: RenderCondition[];
};

export type MultiSelector = {
  id: string;
  displayName: string;
  options: { key: string; value: string }[];
  selected: string[];
  hint?: string;
  renderConditions?: RenderCondition[];
};

export type InputMappings = {
  ungrouped: { [key: string]: InputMapping };
  grouped: { [key: string]: InputMappingGroup };
  lists: {
    [listName: string]: InputMappingList;
  };
  multiselectors?: { [key: string]: MultiSelector };
};

export type OutputMapping = {
  id: string;
  displayName: string;
  assignedTo: string;
  selected: boolean;
  hint?: string | ReactNode;
  type?: string | null;
  deprecation_text?: string;
};

export type OutputMappings = {
  [DEFAULT_OUTPUT_BE_NAME]: OutputMapping;
  insights: { [key: string]: OutputMapping };
};

export type IntegrationResource = ConnectNodeBase & {
  providerResource: IntegrationProviderResource;
  input: InputMappings;
  output: OutputMappings;
  config: Config;
  files?: { [key: string]: OutputMapping };
  groupedGroupsAndLists?: boolean;
};
